import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SectionTitle, KpiCard } from "@/components/dashboard/widgets";
import {
  Loader2, ShieldCheck, AlertTriangle, FileWarning, TrendingDown,
  Inbox, ScanSearch, ListChecks, Trash2, RefreshCw, Filter,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";

/**
 * Pack D — Admin Compliance Centre.
 * Tabs: Overview · DSAR · Fraud signals · Churn risk
 */
export default function ComplianceCenter() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);

  const refresh = () => api.get("/admin/compliance-center/overview").then((r) => setOverview(r.data));
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-compliance-center">
      <SectionTitle
        eyebrow="Compliance"
        title="Command Centre"
        subtitle="Consent quality, DSARs, fraud signals and churn risk — every lever to keep the platform clean and FCA-defensible."
      />

      {/* KPI strip */}
      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Open DSARs" value={overview.dsar.pending}     icon={Inbox}        accent={overview.dsar.sla_breached > 0 ? "rose" : "blue"} testid="cc-kpi-dsar" />
          <KpiCard label="Open events" value={overview.events.open}     icon={FileWarning}  accent={overview.events.critical > 0 ? "rose" : "blue"}  testid="cc-kpi-events" />
          <KpiCard label="Fraud high (24h)" value={overview.scoring.fraud_high_24h} icon={AlertTriangle} accent={overview.scoring.fraud_high_24h > 0 ? "amber" : "emerald"} testid="cc-kpi-fraud" />
          <KpiCard label="Consent blocks (24h)" value={overview.scoring.consent_block_24h} icon={ShieldCheck} accent={overview.scoring.consent_block_24h > 0 ? "amber" : "emerald"} testid="cc-kpi-consent" />
        </div>
      )}

      <div className="inline-flex gap-1 bg-white border border-slate-200 rounded-full p-1 flex-wrap">
        {[
          ["overview", "Overview"],
          ["dsar",     "DSAR queue"],
          ["fraud",    "Fraud signals"],
          ["churn",    "Churn risk"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`cc-tab-${k}`}
            className={`px-4 py-1.5 text-[12.5px] rounded-full transition-colors ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab overview={overview} />}
      {tab === "dsar"     && <DsarTab onChange={refresh} />}
      {tab === "fraud"    && <FraudTab />}
      {tab === "churn"    && <ChurnTab />}
    </div>
  );
}

function OverviewTab({ overview }) {
  if (!overview) return <Skel />;
  return (
    <Card className="rounded-xl border-slate-200 shadow-soft">
      <CardContent className="p-6 space-y-4">
        <h3 className="font-medium text-slate-900">Scoring activity</h3>
        <div className="grid sm:grid-cols-3 gap-4 text-sm text-slate-700">
          <Stat label="Leads scored · 7d" value={overview.scoring.scored_7d.toLocaleString()} />
          <Stat label="Fraud-high (24h)"  value={overview.scoring.fraud_high_24h} tone={overview.scoring.fraud_high_24h ? "warn" : "good"} />
          <Stat label="Consent blocks (24h)" value={overview.scoring.consent_block_24h} tone={overview.scoring.consent_block_24h ? "warn" : "good"} />
        </div>
        <p className="text-[12.5px] text-slate-500 leading-relaxed">
          Every accepted lead is consent-scored (0–100) and fraud-scored (0–100) at insert time. Leads scoring
          <code className="mx-1 text-[12px] text-slate-700 bg-slate-100 px-1 py-0.5 rounded">consent_tier: flag/block</code> or
          <code className="mx-1 text-[12px] text-slate-700 bg-slate-100 px-1 py-0.5 rounded">fraud_tier: med/high</code> are
          surfaced here automatically + raise a compliance_event for the admin queue.
        </p>
      </CardContent>
    </Card>
  );
}

// ============================ DSAR ============================
function DsarTab({ onChange }) {
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/dsar?status=${status}`).then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {["all", "new", "verifying", "in_progress", "completed", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`cc-dsar-filter-${s}`}
            className={`px-3 py-1 text-[12px] rounded-full capitalize ${status === s ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {loading ? <Skel /> : rows.length === 0 ? (
        <EmptyCard icon={Inbox} title="No DSARs" body="When a data subject access request comes in via the public form, it'll appear here with a 30-day SLA timer." />
      ) : (
        <Card className="rounded-xl border-slate-200 shadow-soft">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-widest text-slate-500 text-left">
                  <tr>
                    <th className="py-2.5 px-4 font-medium">Requestor</th>
                    <th className="font-medium">Type</th>
                    <th className="font-medium">Status</th>
                    <th className="font-medium">SLA</th>
                    <th className="font-medium pr-4 text-right">Filed</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => setActive(r.id)}
                      className="border-t border-slate-100 hover:bg-slate-50/40 cursor-pointer"
                      data-testid={`cc-dsar-row-${r.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900">{r.full_name}</div>
                        <div className="text-[12px] text-slate-500">{r.email}</div>
                      </td>
                      <td className="capitalize">{r.type}</td>
                      <td>
                        <Badge variant="outline" className="capitalize">{(r.status || "").replace("_", " ")}</Badge>
                      </td>
                      <td>
                        {r.sla_breached ? (
                          <span className="text-[12px] font-semibold text-rose-600">SLA breached</span>
                        ) : r.days_remaining !== null ? (
                          <span className={`text-[12px] ${r.days_remaining <= 7 ? "text-amber-600 font-semibold" : "text-slate-600"}`}>
                            {r.days_remaining}d left
                          </span>
                        ) : "—"}
                      </td>
                      <td className="text-right pr-4 text-slate-500 text-[12.5px]">{new Date(r.created_at).toLocaleDateString("en-GB")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <DsarDetailSheet id={active} onClose={() => { setActive(null); load(); onChange?.(); }} />
    </div>
  );
}

function DsarDetailSheet({ id, onClose }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyErase, setBusyErase] = useState(false);

  useEffect(() => {
    if (!id) { setData(null); return; }
    api.get(`/admin/dsar/${id}`).then((r) => { setData(r.data); setStatus(r.data.status); });
  }, [id]);

  if (!id) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/dsar/${id}`, { status, note: note.trim() || undefined });
      toast.success("DSAR updated");
      setNote("");
      const r = await api.get(`/admin/dsar/${id}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const erase = async () => {
    if (!window.confirm(`Permanently anonymise every record for ${data.email}? This cannot be undone.`)) return;
    setBusyErase(true);
    try {
      const r = await api.post(`/admin/dsar/${id}/erase`);
      toast.success(`Erasure complete — ${r.data.records_scrubbed} records anonymised`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erasure failed");
    } finally { setBusyErase(false); }
  };

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="cc-dsar-sheet">
        <SheetHeader>
          <SheetTitle>DSAR · {data?.full_name || "Loading…"}</SheetTitle>
          <SheetDescription>{data?.email}</SheetDescription>
        </SheetHeader>
        {!data ? <Skel /> : (
          <div className="space-y-5 mt-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Mini label="Type"      value={<span className="capitalize">{data.type}</span>} />
              <Mini label="Filed"     value={new Date(data.created_at).toLocaleString("en-GB")} />
              <Mini label="SLA"       value={data.sla_breached ? <span className="text-rose-600 font-semibold">BREACHED</span> : `${data.days_remaining} days remaining`} />
              <Mini label="Matched leads" value={data.leads_count?.toLocaleString() || 0} />
            </div>
            {data.statement && (
              <Card className="rounded-lg border-slate-200">
                <CardContent className="p-4 text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-wrap">{data.statement}</CardContent>
              </Card>
            )}

            <div className="space-y-2">
              <Label>Status</Label>
              <select
                value={status} onChange={(e) => setStatus(e.target.value)}
                className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                data-testid="cc-dsar-status"
              >
                {["new","verifying","in_progress","completed","rejected"].map((s) => (
                  <option key={s} value={s}>{s.replace("_"," ")}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Add an internal note</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Verification email sent / identity confirmed / response pack drafted…" data-testid="cc-dsar-note" />
            </div>

            {Array.isArray(data.notes) && data.notes.length > 0 && (
              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-widest text-slate-500">Audit trail</Label>
                <div className="space-y-2">
                  {data.notes.map((n, i) => (
                    <div key={i} className="text-[12.5px] text-slate-700 rounded-md bg-slate-50 border border-slate-100 p-2.5">
                      <div className="text-[10.5px] text-slate-500">{n.by} · {new Date(n.at).toLocaleString("en-GB")}</div>
                      <div className="mt-0.5">{n.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <Button onClick={save} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="cc-dsar-save">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <ListChecks className="size-4" />} Save
              </Button>
              {data.type === "erasure" && !data.erasure_executed_at && (
                <Button onClick={erase} disabled={busyErase} variant="outline" className="rounded-full text-rose-600 hover:bg-rose-50 border-rose-200" data-testid="cc-dsar-erase">
                  {busyErase ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />} Execute erasure
                </Button>
              )}
              {data.erasure_executed_at && (
                <span className="text-[12px] text-emerald-600 font-medium">
                  Erasure executed · {data.erasure_records_count} records anonymised
                </span>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ============================ Fraud ============================
function FraudTab() {
  const [days, setDays] = useState(7);
  const [minScore, setMinScore] = useState(40);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/fraud-signals?days=${days}&min_score=${minScore}`)
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [days, minScore]);

  const rescan = async () => {
    setRescanning(true);
    try {
      const r = await api.post(`/admin/fraud/rescan?days=1`);
      toast.success(`Rescanned ${r.data.scanned} leads — ${r.data.flagged} flagged`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rescan failed");
    } finally { setRescanning(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[11.5px] text-slate-500 flex items-center gap-1.5"><Filter className="size-3.5" /> Window</div>
        {[1, 7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 text-[12px] rounded-full ${days === d ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600"}`} data-testid={`cc-fraud-days-${d}`}>{d}d</button>
        ))}
        <div className="text-[11.5px] text-slate-500 ml-3">Min score</div>
        <Input type="number" min={0} max={100} value={minScore} onChange={(e) => setMinScore(parseInt(e.target.value || "0"))} className="w-20 h-8 text-sm" data-testid="cc-fraud-min-score" />
        <div className="ml-auto" />
        <Button onClick={rescan} disabled={rescanning} variant="outline" className="rounded-full" data-testid="cc-fraud-rescan">
          {rescanning ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />} Rescan last 24h
        </Button>
      </div>

      {loading || !data ? <Skel /> : (
        <>
          {data.top_flags.length > 0 && (
            <Card className="rounded-xl border-slate-200 shadow-soft">
              <CardContent className="p-6">
                <h3 className="font-medium text-slate-900 mb-3">Top patterns</h3>
                <div className="flex flex-wrap gap-2">
                  {data.top_flags.map((f) => (
                    <span key={f.flag} className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100 px-3 py-1 text-[11.5px] font-medium">
                      {f.flag.replace(/_/g, " ")} <span className="text-rose-500 font-mono">×{f.count}</span>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {data.rows.length === 0 ? (
            <EmptyCard icon={ScanSearch} title="Clean window" body={`No leads scored ≥${minScore} in the last ${days} days. Lower the threshold to inspect borderline traffic.`} />
          ) : (
            <Card className="rounded-xl border-slate-200 shadow-soft">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-widest text-slate-500 text-left">
                      <tr>
                        <th className="py-2.5 px-4 font-medium">Lead</th>
                        <th className="font-medium">Sector</th>
                        <th className="font-medium">Publisher</th>
                        <th className="font-medium text-right">Fraud</th>
                        <th className="font-medium text-right">Consent</th>
                        <th className="font-medium">Flags</th>
                        <th className="font-medium text-right pr-4">Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.slice(0, 50).map((r) => (
                        <tr key={r.id} className="border-t border-slate-100" data-testid={`cc-fraud-row-${r.id}`}>
                          <td className="py-2.5 px-4 text-[12.5px] font-mono text-slate-600">{r.id.slice(0, 8)}…</td>
                          <td className="capitalize">{(r.sector || "").replace("-", " ")}</td>
                          <td className="text-[12px] text-slate-600">{r.publisher_email || "—"}</td>
                          <td className={`text-right tabular-nums font-medium ${r.fraud_tier === "high" ? "text-rose-600" : r.fraud_tier === "med" ? "text-amber-600" : "text-slate-700"}`}>{r.fraud_score}</td>
                          <td className={`text-right tabular-nums ${r.consent_tier === "block" ? "text-rose-600" : r.consent_tier === "flag" ? "text-amber-600" : "text-slate-700"}`}>{r.consent_score ?? "—"}</td>
                          <td className="text-[11px] text-slate-500 max-w-[260px] truncate">{(r.fraud_flags || []).concat(r.consent_flags || []).slice(0, 4).join(" · ")}</td>
                          <td className="text-right pr-4 text-[12px] text-slate-500">{new Date(r.created_at).toLocaleString("en-GB")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ============================ Churn ============================
function ChurnTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/churn-risk?days=${days}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[11.5px] text-slate-500 flex items-center gap-1.5"><Filter className="size-3.5" /> Window</div>
        {[14, 30, 60, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 text-[12px] rounded-full ${days === d ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600"}`} data-testid={`cc-churn-days-${d}`}>{d}d</button>
        ))}
      </div>

      {loading || !data ? <Skel /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <TinyKpi label="High risk"  value={data.summary.high}  tone="rose" />
            <TinyKpi label="Med risk"   value={data.summary.med}   tone="amber" />
            <TinyKpi label="Low risk"   value={data.summary.low}   tone="slate" />
            <TinyKpi label="Clean"      value={data.summary.clean} tone="emerald" />
          </div>

          {data.rows.length === 0 ? (
            <EmptyCard icon={TrendingDown} title="No buyer activity" body="No buyers had measurable activity in the selected window." />
          ) : (
            <Card className="rounded-xl border-slate-200 shadow-soft">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-widest text-slate-500 text-left">
                      <tr>
                        <th className="py-2.5 px-4 font-medium">Buyer</th>
                        <th className="font-medium text-right">Risk</th>
                        <th className="font-medium">Drivers</th>
                        <th className="font-medium text-right">Spend Δ</th>
                        <th className="font-medium text-right pr-4">Silent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.slice(0, 40).map((r) => (
                        <tr key={r.user_id} className="border-t border-slate-100" data-testid={`cc-churn-row-${r.user_id}`}>
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-900">{r.company || r.name || r.email}</div>
                            <div className="text-[12px] text-slate-500">{r.email}</div>
                          </td>
                          <td className={`text-right tabular-nums font-semibold ${r.tier === "high" ? "text-rose-600" : r.tier === "med" ? "text-amber-600" : "text-slate-700"}`}>
                            {r.score}
                          </td>
                          <td className="text-[12px] text-slate-600 max-w-[360px]">
                            {r.drivers.length === 0 ? "—" : r.drivers.join(" · ")}
                          </td>
                          <td className="text-right tabular-nums text-[12.5px]">
                            <span className="text-slate-700">£{r.spend_last7}</span>
                            <span className="text-slate-400 ml-1">/ £{r.spend_prev7}</span>
                          </td>
                          <td className="text-right pr-4 text-[12.5px] text-slate-600">{r.days_silent ?? "—"}d</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ============================ helpers ============================
function Skel() { return <div className="grid place-items-center py-20"><Loader2 className="size-5 text-slate-400 animate-spin" /></div>; }

function EmptyCard({ icon: Icon, title, body }) {
  return (
    <Card className="rounded-xl border-dashed border-slate-300 bg-slate-50/40">
      <CardContent className="p-10 text-center space-y-2">
        <Icon className="size-6 text-slate-300 mx-auto" />
        <h3 className="font-medium text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">{body}</p>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }) {
  const tones = { warn: "text-amber-700", good: "text-emerald-700" };
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-[20px] font-semibold mt-1 ${tones[tone] || "text-slate-900"}`}>{value}</div>
    </div>
  );
}
function Mini({ label, value }) { return (
  <div>
    <div className="text-[10.5px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="text-[14px] text-slate-900 mt-0.5">{value}</div>
  </div>
); }
function TinyKpi({ label, value, tone }) {
  const m = { rose: "bg-rose-50 text-rose-700 border-rose-100",
              amber: "bg-amber-50 text-amber-700 border-amber-100",
              slate: "bg-slate-50 text-slate-700 border-slate-200",
              emerald: "bg-emerald-50 text-emerald-700 border-emerald-100" }[tone] || "bg-white";
  return (
    <div className={`rounded-lg border px-4 py-3 ${m}`}>
      <div className="text-[10.5px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-[22px] font-semibold tabular-nums mt-1">{value}</div>
    </div>
  );
}
